import React, { useCallback, useMemo, useRef, useState } from 'react';

type Props = {
    images: string[];
    videoUrl?: string;
    alt: string;
};

type MediaItem = { type: 'video'; src: string } | { type: 'image'; src: string };

const ImageGallery: React.FC<Props> = ({ images, videoUrl, alt }) => {
    const media = useMemo<MediaItem[]>(() => {
        const items: MediaItem[] = [];

        if (videoUrl) {
            items.push({ type: 'video', src: videoUrl });
        }

        for (const src of images) {
            items.push({ type: 'image', src });
        }

        return items;
    }, [images, videoUrl]);

    const [current, setCurrent] = useState(0);
    const touchStartX = useRef(0);
    const touchDeltaX = useRef(0);

    const goTo = useCallback((idx: number) => {
        setCurrent(Math.max(0, Math.min(idx, media.length - 1)));
    }, [media.length]);

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
        touchDeltaX.current = 0;
    };

    const onTouchMove = (e: React.TouchEvent) => {
        touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    };

    const onTouchEnd = () => {
        if (Math.abs(touchDeltaX.current) > 40) {
            if (touchDeltaX.current < 0) goTo(current + 1);
            else goTo(current - 1);
        }
        touchDeltaX.current = 0;
    };

    if (media.length === 0) {
        return <div className="image-placeholder" aria-hidden="true" />;
    }

    // Single item — no carousel needed
    if (media.length === 1) {
        const item = media[0];
        return item.type === 'video' ? (
            <video
                className="product-hero"
                src={item.src}
                controls
                playsInline
                preload="metadata"
            />
        ) : (
            <img className="product-hero" src={item.src} alt={alt} />
        );
    }

    // Multiple items — carousel
    const currentItem = media[current];

    return (
        <div
            className="gallery"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <div className="gallery-viewport">
                {currentItem.type === 'video' ? (
                    <video
                        className="gallery-media"
                        src={currentItem.src}
                        controls
                        playsInline
                        preload="metadata"
                    />
                ) : (
                    <img className="gallery-media" src={currentItem.src} alt={`${alt} ${current + 1}`} />
                )}

                {current > 0 && (
                    <button className="gallery-arrow gallery-arrow-left" type="button" onClick={() => goTo(current - 1)} aria-label="Precedente">
                        ‹
                    </button>
                )}
                {current < media.length - 1 && (
                    <button className="gallery-arrow gallery-arrow-right" type="button" onClick={() => goTo(current + 1)} aria-label="Successiva">
                        ›
                    </button>
                )}
            </div>

            <div className="gallery-dots">
                {media.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        className={`gallery-dot ${i === current ? 'active' : ''}`}
                        onClick={() => goTo(i)}
                        aria-label={`${media[i].type === 'video' ? 'Video' : 'Immagine'} ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default ImageGallery;
